import React from 'react';
import { useTranslation } from 'react-i18next';

interface ReleaseProgressBarProps {
  status: string;
  rejectReason?: string;
  className?: string;
}

const ReleaseProgressBar: React.FC<ReleaseProgressBarProps> = ({
  status,
  rejectReason,
  className = ''
}) => {
  const { t } = useTranslation();

  const getProgressSteps = () => {
    const steps = [
      {
        id: 'draft',
        title: t('progress.draft'),
        description: t('progress.draftDesc'),
        icon: '📝'
      },
      {
        id: 'pending',
        title: t('progress.submitted'),
        description: t('progress.submittedDesc'),
        icon: '⏳'
      },
      {
        id: 'review',
        title: t('progress.review'),
        description: t('progress.reviewDesc'),
        icon: '👀'
      },
      {
        id: 'approved',
        title: t('progress.approved'),
        description: t('progress.approvedDesc'),
        icon: '✅'
      },
      {
        id: 'published',
        title: t('progress.published'),
        description: t('progress.publishedDesc'),
        icon: '🎵'
      }
    ];

    return steps;
  };

  const getCurrentStepIndex = () => {
    switch (status) {
      case 'draft':
        return 0;
      case 'pending':
      case 'submitted':
        return 1;
      case 'approved':
        return 3;
      case 'published':
        return 4;
      case 'rejected':
        return 2; // Rejected durumunda review adımında kalıyor
      default:
        return 0;
    }
  };

  const getStepStatus = (stepIndex: number) => {
    const currentIndex = getCurrentStepIndex();
    
    if (status === 'rejected' && stepIndex === 2) {
      return 'rejected';
    }
    
    if (stepIndex < currentIndex) {
      return 'completed';
    } else if (stepIndex === currentIndex) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  const getStepColor = (stepStatus: string) => {
    switch (stepStatus) {
      case 'completed':
        return 'bg-green-500 border-green-500';
      case 'current':
        return 'bg-orange-500 border-orange-500';
      case 'rejected':
        return 'bg-red-500 border-red-500';
      case 'pending':
      default:
        return 'bg-white/10 border-white/20';
    }
  };

  const getStepTextColor = (stepStatus: string) => {
    switch (stepStatus) {
      case 'completed':
        return 'text-green-300';
      case 'current':
        return 'text-orange-300';
      case 'rejected':
        return 'text-red-300';
      case 'pending':
      default:
        return 'text-white/60';
    }
  };



  const steps = getProgressSteps();

  return (
    <div className={`${className}`}>
      <div className="relative mb-6">
        {/* Progress Line Background */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-white/20"></div>
        
        {/* Progress Line Active */}
        <div 
          className={`absolute top-5 left-0 h-0.5 transition-all duration-500 ${
            status === 'rejected' ? 'bg-red-500' : 'bg-green-500'
          }`}
          style={{ width: `${(getCurrentStepIndex() / (steps.length - 1)) * 100}%` }}
        ></div>
        
        {/* Steps */}
        <div className="grid grid-cols-5 gap-0">
          {steps.map((step, index) => {
            const stepStatus = getStepStatus(index);
            
            return (
              <div key={step.id} className="flex flex-col items-center">
                {/* Step Circle */}
                <div
                  className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-lg font-medium transition-all duration-300 relative z-10 ${getStepColor(stepStatus)}`}
                >
                  {stepStatus === 'completed' ? (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : stepStatus === 'rejected' ? (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="text-sm">
                      {step.icon}
                    </span>
                  )}
                </div>
                
                {/* Step Info */}
                <div className="mt-3 text-center px-1">
                  <div className={`text-sm font-medium ${getStepTextColor(stepStatus)}`}>
                    {step.title}
                  </div>
                  <div className="text-xs text-white/50 mt-1">
                    {step.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Reject Reason */}
      {status === 'rejected' && rejectReason && (
        <div className="mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="h-5 w-5 text-red-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-red-200">
                {t('progress.rejectReason')}
              </h4>
              <p className="text-sm text-red-100 mt-1">
                {rejectReason}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Current Status Info */}
      <div className="mt-4 text-center">
        <div className="text-sm text-white/70">
          {status === 'draft' && t('progress.statusDraft')}
          {status === 'pending' && t('progress.statusPending')}
          {status === 'submitted' && t('progress.statusSubmitted')}
          {status === 'approved' && t('progress.statusApproved')}
          {status === 'published' && t('progress.statusPublished')}
          {status === 'rejected' && t('progress.statusRejected')}
        </div>
      </div>
    </div>
  );
};

export default ReleaseProgressBar; 